import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';

const FavouritesContext = createContext(null);
export const useTileFavourites = () => useContext(FavouritesContext);

const sigOf = (registry, favs) =>
  favs.map((id) => {
    const p = registry[id];
    return p ? `${id}|${p.title}|${p.subtitle}|${p.badge}|${p.badgeTone}` : `${id}|-`;
  }).join('||');

export function TileFavouritesProvider({ children }) {
  const [favs, setFavs] = useState([]);
  const [tileOrder, setTileOrder] = useState({});
  const [sectionOrder, setSectionOrderState] = useState([]);
  const [hidden, setHidden] = useState({ tiles: [], sections: [] });
  const [collapsed, setCollapsed] = useState([]);
  const [layout, setLayout] = useState({ sections: [], titles: {}, tile_sections: {}, descriptions: {} });
  const [customising, setCustomising] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/staff/dashboard-layout`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : null)).then((d) => d && setLayout(d)).catch(() => {});
  }, []);
  const saveLayout = useCallback(async (patch) => {
    const r = await fetch(`${API_URL}/api/staff/dashboard-layout`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
    if (!r.ok) { toast.error('Could not save dashboard layout'); return; }
    setLayout(await r.json());
    toast.success('Dashboard layout saved for everyone');
  }, []);
  const [loaded, setLoaded] = useState(false);
  const registry = useRef({});
  const [sig, setSig] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/staff/dashboard-favourites`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { tiles: [] }))
      .then((d) => { setFavs(d.tiles || []); setTileOrder(d.tile_order || {}); setSectionOrderState(d.section_order || []); setHidden({ tiles: d.hidden?.tiles || [], sections: d.hidden?.sections || [] }); setCollapsed(d.collapsed || []); })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    const next = sigOf(registry.current, favs);
    if (next !== sig) setSig(next);
  });

  const persist = useCallback((tiles) => {
    setFavs(tiles);
    fetch(`${API_URL}/api/staff/dashboard-favourites`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tiles }),
    }).catch(() => toast.error('Could not save favourites'));
  }, []);

  const orderRef = useRef(tileOrder);
  orderRef.current = tileOrder;
  const setSectionOrder = useCallback((sectionId, ids) => {
    const next = { ...orderRef.current };
    if (ids?.length) next[sectionId] = ids; else delete next[sectionId];
    setTileOrder(next);
    fetch(`${API_URL}/api/staff/dashboard-favourites`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tile_order: next }),
    }).then((r) => { if (!r.ok) throw new Error(); }).catch(() => toast.error('Could not save tile order'));
  }, []);

  const saveSectionOrder = useCallback((ids) => {
    setSectionOrderState(ids);
    fetch(`${API_URL}/api/staff/dashboard-favourites`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ section_order: ids }),
    }).then((r) => { if (!r.ok) throw new Error(); }).catch(() => toast.error('Could not save section order'));
  }, []);

  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;
  const toggleCollapsed = useCallback((sectionId) => {
    const cur = collapsedRef.current;
    const next = cur.includes(sectionId) ? cur.filter((x) => x !== sectionId) : [...cur, sectionId];
    setCollapsed(next);
    fetch(`${API_URL}/api/staff/dashboard-favourites`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ collapsed: next }),
    }).then((r) => { if (!r.ok) throw new Error(); }).catch(() => toast.error('Could not save'));
  }, []);

  const setCollapsedAll = useCallback((ids) => {
    setCollapsed(ids);
    fetch(`${API_URL}/api/staff/dashboard-favourites`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ collapsed: ids }),
    }).then((r) => { if (!r.ok) throw new Error(); }).catch(() => toast.error('Could not save'));
  }, []);

  const resetAll = useCallback(async () => {
    const empty = { tiles: [], tile_order: {}, section_order: [], hidden: { tiles: [], sections: [] }, collapsed: [] };
    setFavs([]); setTileOrder({}); setSectionOrderState([]); setHidden(empty.hidden); setCollapsed([]);
    try {
      const r = await fetch(`${API_URL}/api/staff/dashboard-favourites`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(empty) });
      if (!r.ok) throw new Error();
      toast.success('Dashboard reset to the default layout');
    } catch { toast.error('Could not reset your dashboard'); }
  }, []);

  const hiddenRef = useRef(hidden);
  hiddenRef.current = hidden;
  const toggleHidden = useCallback((kind, id) => {
    const cur = hiddenRef.current;
    const list = cur[kind] || [];
    const on = list.includes(id);
    const next = { ...cur, [kind]: on ? list.filter((x) => x !== id) : [...list, id] };
    setHidden(next);
    fetch(`${API_URL}/api/staff/dashboard-favourites`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ hidden: next }),
    }).then((r) => { if (!r.ok) throw new Error(); }).catch(() => toast.error('Could not save'));
    toast.success(on ? (kind === 'tiles' ? 'Tile shown again' : 'Section shown again') : (kind === 'tiles' ? 'Tile hidden — use "Show hidden" to bring it back' : 'Section hidden — use "Show hidden" to bring it back'));
  }, []);

  const favsRef = useRef(favs);
  favsRef.current = favs;

  const toggle = useCallback((id) => {
    const cur = favsRef.current;
    const on = cur.includes(id);
    persist(on ? cur.filter((x) => x !== id) : [...cur, id]);
    toast.success(on ? 'Removed from favourites' : 'Pinned to Favourites at the top');
  }, [persist]);

  const move = useCallback((id, dir) => {
    const cur = favsRef.current;
    const i = cur.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= cur.length) return;
    const next = [...cur];
    [next[i], next[j]] = [next[j], next[i]];
    persist(next);
  }, [persist]);

  const register = (id, props) => { registry.current[id] = props; };

  return (
    <FavouritesContext.Provider value={{ favs, loaded, toggle, move, register, registry, sig, tileOrder, setSectionOrder, sectionOrder, saveSectionOrder, hidden, toggleHidden, collapsed, toggleCollapsed, setCollapsedAll, resetAll, layout, saveLayout, customising, setCustomising }}>
      {children}
    </FavouritesContext.Provider>
  );
}

export function FavouriteStar({ id }) {
  const ctx = useTileFavourites();
  if (!ctx) return null;
  const on = ctx.favs.includes(id);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); ctx.toggle(id); }}
      aria-label={on ? 'Remove from favourites' : 'Add to favourites'}
      title={on ? 'Remove from favourites' : 'Add to favourites'}
      data-testid={`${id}-fav-toggle`}
      className={`absolute top-1 right-1 p-1 rounded-md transition-opacity ${on ? 'text-amber-500 opacity-100' : 'text-gray-300 hover:text-amber-500 opacity-70 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100'}`}
    >
      <Star className={`w-3.5 h-3.5 ${on ? 'fill-amber-400' : ''}`} />
    </button>
  );
}

export function FavouritesPanel({ renderTile }) {
  const ctx = useTileFavourites();
  if (!ctx || !ctx.loaded) return null;
  const items = ctx.favs.map((id) => [id, ctx.registry.current[id]]).filter(([, p]) => p);
  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 mt-4 sm:mt-5" data-testid="favourites-panel">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-600">
          <Star className="w-3.5 h-3.5 fill-amber-400" />
        </span>
        <h2 className="text-base sm:text-lg font-extrabold text-gray-900 tracking-tight">Favourites</h2>
        {items.length > 0 && <span className="text-[11px] text-gray-400">· use the arrows to reorder</span>}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-gray-500 bg-white border border-dashed border-gray-300 rounded-xl px-3 py-2.5" data-testid="favourites-empty">
          Star <Star className="inline w-3 h-3 -mt-0.5 text-amber-500" /> any tile below to pin it here for quick access.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {items.map(([id, props], i) => (
            <div key={id} className="relative group" data-testid={`fav-${id}`}>
              {renderTile({ ...props, testId: `fav-${id}-tile`, favouriteOf: id })}
              <div className="absolute bottom-1 right-1 flex gap-0.5 bg-white/90 border border-gray-200 rounded-md px-0.5">
                <button type="button" onClick={() => ctx.move(id, -1)} disabled={i === 0} aria-label="Move left" data-testid={`fav-${id}-left`}
                  className="p-0.5 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-25 disabled:hover:bg-transparent"><ChevronLeft className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => ctx.move(id, 1)} disabled={i === items.length - 1} aria-label="Move right" data-testid={`fav-${id}-right`}
                  className="p-0.5 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-25 disabled:hover:bg-transparent"><ChevronRight className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
